import React, { useMemo } from 'react';
import { useScreenSize } from '../../hooks/useScreenSize';

export type PageLayoutProps = {
  children: React.ReactNode;
  /**
   * Maximum width of the content area
   * @default '7xl' (1280px)
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  /**
   * Horizontal padding
   * @default 'md' (responsive: 1rem mobile, 1.5rem tablet, 2rem desktop)
   */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Vertical padding
   * @default 'md' (1.5rem)
   */
  verticalPadding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Skip top padding (useful when first child is a header that should be flush with top)
   * @default false
   */
  skipTopPadding?: boolean;
  /**
   * Additional className for the container
   */
  className?: string;
};

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
};

const verticalPaddingClasses = {
  none: 'py-0',
  sm: 'py-3',
  md: 'py-6',
  lg: 'py-8',
  xl: 'py-12',
};

/**
 * Calculate dynamic padding based on screen width.
 * Uses CSS custom properties for smooth, performant transitions.
 * 
 * For admin pages (full width), padding reduces more aggressively at smaller sizes
 * to prevent large gaps between sidebar and content.
 */
function calculatePadding(width: number, padding: string, maxWidth: string): React.CSSProperties {
  if (padding === 'none') {
    return { paddingLeft: 0, paddingRight: 0 };
  }

  // Convert rem to px (assuming 16px base)
  const remToPx = (rem: number) => rem * 16;

  if (maxWidth === 'full') {
    // Admin pages: aggressive padding reduction to maximize content space
    // Sidebar takes 256px (open) or 64px (closed), so we need to be conservative
    if (width < 768) return { paddingLeft: remToPx(1), paddingRight: remToPx(1) }; // 16px
    if (width < 1024) return { paddingLeft: remToPx(1.25), paddingRight: remToPx(1.25) }; // 20px
    if (width < 1280) return { paddingLeft: remToPx(1.5), paddingRight: remToPx(1.5) }; // 24px (was 32px)
    if (width < 1536) return { paddingLeft: remToPx(2), paddingRight: remToPx(2) }; // 32px
    return { paddingLeft: remToPx(2.5), paddingRight: remToPx(2.5) }; // 40px
  } else {
    // Owner pages: standard responsive padding
    const paddingMap: Record<string, (w: number) => number> = {
      sm: (w) => w < 640 ? remToPx(0.75) : remToPx(1),
      md: (w) => {
        if (w < 640) return remToPx(1);
        if (w < 768) return remToPx(1.25);
        if (w < 1024) return remToPx(1.5);
        if (w < 1280) return remToPx(2);
        return remToPx(2);
      },
      lg: (w) => {
        if (w < 640) return remToPx(1.5);
        if (w < 768) return remToPx(2);
        if (w < 1024) return remToPx(2.5);
        if (w < 1280) return remToPx(3);
        return remToPx(3);
      },
      xl: (w) => {
        if (w < 640) return remToPx(2);
        if (w < 768) return remToPx(3);
        if (w < 1024) return remToPx(4);
        if (w < 1280) return remToPx(5);
        return remToPx(5);
      },
    };

    const paddingFn = paddingMap[padding] || paddingMap.md;
    const px = paddingFn(width);
    return { paddingLeft: px, paddingRight: px };
  }
}

/**
 * PageLayout provides consistent content padding and max-width constraints.
 * 
 * Uses dynamic padding calculation based on actual screen width for smooth,
 * responsive spacing that adapts fluidly without large gaps.
 * 
 * @example
 * ```tsx
 * <PageLayout maxWidth="7xl" padding="md">
 *   <h1>Page Title</h1>
 *   <p>Page content...</p>
 * </PageLayout>
 * ```
 */
export function PageLayout({
  children,
  maxWidth = '7xl',
  padding = 'md',
  verticalPadding = 'md',
  skipTopPadding = false,
  className = '',
}: PageLayoutProps) {
  const { width } = useScreenSize();
  const maxWidthClass = maxWidthClasses[maxWidth];
  const verticalPaddingClass = verticalPaddingClasses[verticalPadding];

  // Calculate dynamic padding based on actual screen width
  // Memoized to prevent recalculation on every render
  const paddingStyle = useMemo(
    () => calculatePadding(width, padding, maxWidth),
    [width, padding, maxWidth]
  );

  // For 'full' maxWidth, don't center - let content fill available space
  const containerClass = maxWidth === 'full' 
    ? `w-full ${maxWidthClass}`
    : `mx-auto ${maxWidthClass} w-full`;

  // If verticalPadding is 'none', don't add any padding classes
  const hasVerticalPadding = verticalPadding !== 'none';
  
  // Calculate vertical padding for CSS variable (convert Tailwind spacing to rem)
  const verticalPaddingMap: Record<string, string> = {
    sm: '0.75rem',  // py-3 = 0.75rem
    md: '1.5rem',   // py-6 = 1.5rem
    lg: '2rem',     // py-8 = 2rem
    xl: '3rem',     // py-12 = 3rem
  };
  const verticalPaddingValue = hasVerticalPadding 
    ? verticalPaddingMap[verticalPadding] || '1.5rem'
    : '0';
  
  // Build padding classes - skip top padding if skipTopPadding is true
  let paddingClasses = '';
  if (hasVerticalPadding && !skipTopPadding) {
    paddingClasses = verticalPaddingClass;
  } else if (hasVerticalPadding && skipTopPadding) {
    // Only bottom padding
    const bottomPaddingMap: Record<string, string> = {
      sm: 'pb-3',
      md: 'pb-6',
      lg: 'pb-8',
      xl: 'pb-12',
    };
    paddingClasses = bottomPaddingMap[verticalPadding] || 'pb-6';
  }
  
  return (
    <div 
      className={`flex-1 flex flex-col min-h-0 w-full ${paddingClasses} ${className}`}
      style={{ 
        ...paddingStyle, 
        transition: 'padding 0.2s ease-out',
        '--page-padding-left': `${paddingStyle.paddingLeft || 0}px`,
        '--page-padding-right': `${paddingStyle.paddingRight || 0}px`,
        '--page-padding-top': skipTopPadding ? '0' : verticalPaddingValue,
      } as React.CSSProperties & { '--page-padding-left': string; '--page-padding-right': string; '--page-padding-top': string }}
    >
      <div className={`flex-1 flex flex-col min-h-0 ${containerClass} relative`}>
        {children}
      </div>
    </div>
  );
}
